import _ from 'lodash'
import type { BaseDataItem, ResolvedInnerElementsDataItem, RefSchemas, RefSchemasByDataType } from '@wix/thunderbolt-becky-types'
import { VECTOR_ART_DATA_TYPE } from '@wix/thunderbolt-commons'
import { isRefArray } from './shapeIdsNode'
import type { VectorArtDataItem } from '@wix/thunderbolt-becky-root'

/**
 * Extracts SVG URIs from a data item based on schema references.
 * Recursively traverses the data structure to find all SVG URIs.
 *
 * @param schemas - Schema definitions mapping data types to their reference information across all data maps
 * @param dataItem - The data item to extract SVG URIs from
 * @returns Array of SVG URIs found in the data item
 */
export const getSvgsBySchemas = (
	schemas: RefSchemas | undefined,
	dataItem: BaseDataItem | undefined
): string[] => {
	if (!schemas || !dataItem) {
		return []
	}

	const schemaByDataType = Object.assign({}, ...Object.values(schemas))
	const svgUris: Set<string> = new Set()

	const extractSvgUris = (item: BaseDataItem | ResolvedInnerElementsDataItem | VectorArtDataItem | undefined): void => {
		if (!item || typeof item !== 'object') {
			return
		}

		if (Array.isArray(item)) {
			item.forEach((value) => {
				extractSvgUris(value)
			})
			return
		}

		if (item.type === 'InnerElement') {
			const innerElementDataItem = item as unknown as ResolvedInnerElementsDataItem
			extractSvgUris(innerElementDataItem.dataQuery)
			extractSvgUris(innerElementDataItem.styleId)
			extractSvgUris(innerElementDataItem.innerElementsQuery)
			return
		}

		if (isRefArray<any>(item)) {
			item.values.forEach((value) => {
				extractSvgUris(value)
			})
			return
		}

		if (item.type === VECTOR_ART_DATA_TYPE) {
			svgUris.add((item as VectorArtDataItem).uri)
		}

		const schemaRefs = schemaByDataType[item.type]
		if (schemaRefs) {
			// Process each reference in the schema
			for (const refInfo of schemaRefs) {
				const referencedValue = _.get(item, refInfo.path)

				if (referencedValue) {
					extractSvgUris(referencedValue)
				}
			}
		}
	}

	extractSvgUris(dataItem)

	// Remove duplicates and return
	return Array.from(svgUris)
}
